// Campaign Classification catalog — contracts only. This module declares the
// capability shape, its entry/option/metadata types, and the dimension-versus-
// attribute distinction. It contains no concrete axes, values, labels, or
// parent-relationship data — that Classification fixture data lives in the
// mock/seed layer, and the in-memory catalog implementation is its only runtime
// consumer. `CampaignClassificationCatalog` SHALL NOT contain Status: Status is a
// `campaign` table column, defined separately in `campaign.types.ts`.

/** Whether a Classification variable is a registry dimension or a free-value attribute. */
export type CampaignClassificationVariableKind = 'dimension' | 'attribute'

/** One selectable Classification value. `parentCode` carries hierarchy (e.g. a country under a region). */
export interface CampaignClassificationOption {
	readonly code: string
	readonly label: string
	readonly parentCode?: string | null
}

/** One Classification axis's metadata and values, as the Campaign form/filter rail render it. */
export interface CampaignClassificationVariable {
	readonly axisCode: string
	readonly label: string
	readonly kind: CampaignClassificationVariableKind
	readonly options: readonly CampaignClassificationOption[]
}

/**
 * The fixed set of Classification axis codes the Campaign catalog reads, in presentation
 * order. `product` is appended rather than placed beside `brand`: this order IS the
 * presentation contract, so appending leaves the five earlier positions untouched.
 */
// `product` is NOT here: brand and product are two levels of the `brand` axis, so
// requiring a `product` axis fails the whole catalog on a database that has none —
// `resolveRequiredAxisIds` throws before any value is fetched. The Product variable
// is derived from the brand axis instead; see the adapter.
export const CAMPAIGN_CLASSIFICATION_AXIS_CODES = [
	'objective',
	'geography',
	'brand',
	'channel',
	'business_unit',
] as const

/**
 * The resolved snapshot `getCatalog()` returns — `brand` also appears as `addable[0]`, and
 * `product` as `addable`'s last entry: both are form option lists AND addable filter groups.
 */
export interface CampaignClassificationCatalogSnapshot {
	readonly objective: CampaignClassificationVariable
	readonly geography: CampaignClassificationVariable
	readonly brand: CampaignClassificationVariable
	readonly product: CampaignClassificationVariable
	readonly addable: readonly CampaignClassificationVariable[]
}

/**
 * The single, replaceable capability boundary for Campaign Classification data —
 * and only Classification data. Objective and Geography are the fixed filter
 * groups; Brand and Product are each both a form option list and an entry in the
 * curated, dynamically-addable catalog. No Status of any kind is ever supplied here.
 * `getCatalog()` is a single consolidated load bounded to at most seven HTTP
 * requests (one axis-registry read, six value reads) when HTTP-backed.
 *
 * `product` options are flat and unscoped by Brand: the brand-to-product relation is a
 * column on the `product` table, reachable only through `GET /products`, which is outside
 * this capability's axis-registry contract.
 */
export interface CampaignClassificationCatalog {
	getCatalog(): Promise<CampaignClassificationCatalogSnapshot>
}
